import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Gauge, Users, Cog, Zap, BadgeCheck, MessageCircle, FileText } from "lucide-react";
import Viewer360 from "./Viewer360";
import { CONTACT } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";

const VehicleModal = ({ vehicle, onClose, onEnquire }) => {
  const { t } = useLanguage();

  useEffect(() => {
    if (!vehicle) return;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [vehicle, onClose]);

  return (
    <AnimatePresence>
      {vehicle && (
        <motion.div
          data-testid="vehicle-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={vehicle.name}
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92svh] w-full max-w-5xl overflow-y-auto rounded-t-3xl border border-gold/15 bg-[#0c0c10] shadow-2xl sm:rounded-3xl"
          >
            <button
              type="button"
              data-testid="vehicle-modal-close"
              aria-label={t("modal.close")}
              onClick={onClose}
              className="absolute end-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-md transition-colors hover:bg-gold hover:text-[#08080A]"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="grid gap-0 lg:grid-cols-[1.35fr_1fr]">
              <div className="p-4 sm:p-6">
                <Viewer360 vehicle={vehicle} />
              </div>

              <div className="flex flex-col p-6 sm:p-8 lg:ps-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                  {t(`categories.${vehicle.category}`)}
                </p>
                <h3 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">{vehicle.name}</h3>

                <div className="mt-5 flex items-baseline gap-2 border-y border-white/[0.07] py-4">
                  {vehicle.price_per_day ? (
                    <>
                      <span className="font-display text-3xl font-bold text-gold-gradient" data-testid="modal-price">
                        {vehicle.currency} {Number(vehicle.price_per_day).toLocaleString("en-US")}
                      </span>
                      <span className="text-sm text-slate-400">{t("modal.perDay")}</span>
                    </>
                  ) : (
                    <span className="font-display text-2xl font-semibold text-slate-200" data-testid="modal-price">
                      {t("fleet.priceOnRequest")}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500">{t("modal.finalNote")}</p>

                {vehicle.no_deposit_eligible && (
                  <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold/10 px-4 py-2 text-xs font-semibold text-gold" data-testid="modal-nodeposit-badge">
                    <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                    {t("modal.noDepositYes")}
                  </p>
                )}

                <div className="mt-6">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">{t("modal.specs")}</h4>
                  <dl className="mt-3 grid grid-cols-2 gap-3">
                    {[
                      { icon: Cog, label: t("fleet.transmission"), value: vehicle.transmission },
                      { icon: Users, label: t("fleet.seats"), value: vehicle.seats },
                      vehicle.engine && { icon: Gauge, label: t("modal.engine"), value: vehicle.engine },
                      vehicle.acceleration && { icon: Zap, label: t("modal.acceleration"), value: vehicle.acceleration },
                    ]
                      .filter(Boolean)
                      .map((row) => (
                        <div key={row.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                          <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                            <row.icon className="h-3.5 w-3.5 text-gold/70" aria-hidden="true" />
                            {row.label}
                          </dt>
                          <dd className="mt-1 text-sm font-semibold text-white">{row.value}</dd>
                        </div>
                      ))}
                  </dl>
                </div>

                <div className="mt-auto flex flex-col gap-3 pt-8">
                  <button
                    type="button"
                    data-testid="modal-enquire-button"
                    onClick={() => onEnquire(vehicle)}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#08080A] transition-all hover:bg-gold-light hover:shadow-[0_0_35px_rgba(212,175,55,0.35)]"
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    {t("modal.enquireVehicle")}
                  </button>
                  <a
                    data-testid="modal-whatsapp-button"
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/50 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#25D366] transition-all hover:bg-[#25D366] hover:text-[#08080A]"
                  >
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    {t("modal.whatsapp")}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VehicleModal;
